import { AutocompleteProps } from '@mui/material';
import { filter, first, flatMap, isEmpty, isString, join, map, toString, uniq } from 'es-toolkit/compat';
import React, { useState } from 'react';
import { filterSearchOptions } from 'services/matchSort';
import { CaseSearchItem } from 'services/searchIndex';
import { ArrayParam, BooleanParam, NumberParam, StringParam, useQueryParams } from 'use-query-params';
import { useSearchIndex } from 'utils/queryHooks/useSearchIndex';

export const fullSearchKeys = ['caseLabel', 'caseId', 'slides.*.id', 'slides.*.fileName'];

const fullSearchQueryParamConfig = {
  searchTerm: StringParam,
  caseIdsToInclude: ArrayParam,
  slideIdsToInclude: ArrayParam,
  page: NumberParam,
  slidesMode: BooleanParam,
};

interface FullSearchProps {
  enabled?: boolean;
  studyId?: string;
  controlledValue?: string;
  isDrawer?: boolean;
  handleChange?: (event: React.SyntheticEvent<Element, Event>, value: string | CaseSearchItem) => void;
}

// stateful hook for full search
export const useFullSearch = ({ enabled = true, studyId, isDrawer, handleChange }: FullSearchProps = {}) => {
  const [queryParams, setQueryParams] = useQueryParams(fullSearchQueryParamConfig);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<CaseSearchItem[]>([]);
  const [searchHasNoResults, setSearchHasNoResults] = useState<{ [key: string]: boolean }>({});

  const searchQueryState = queryParams.searchTerm;
  const caseIdsToInclude = queryParams.caseIdsToInclude ?? [];
  const slideIdsToInclude = queryParams.slideIdsToInclude ?? [];
  const slidesMode = Boolean(queryParams.slidesMode);

  const searchQuery = searchQueryState
    ? searchQueryState
    : slidesMode
    ? join(slideIdsToInclude, ',')
    : join(caseIdsToInclude, ',');

  const {
    index,
    isSuccess: searchIndexSuccess,
    isLoading: isSearchIndexLoading,
    isError: isErrorSearchIndex,
  } = useSearchIndex({ enabled });
  const allCaseItems: CaseSearchItem[] = index ?? [];
  // if slidesMode is on, we need to flatten the search index to have a single slide per item
  const allSearchItems = slidesMode
    ? flatMap(allCaseItems, (caseItem) => map(caseItem?.slides, (slide) => ({ ...caseItem, slides: [slide] })))
    : allCaseItems;

  const handleSearch = (searchTerm: string) => {
    const filteredOptionsOfSearch = filterSearchOptions<CaseSearchItem>(fullSearchKeys, allSearchItems, searchTerm);
    setFilteredOptions(filteredOptionsOfSearch);
    setSearchHasNoResults((prevValue) => ({ ...prevValue, [searchTerm]: isEmpty(filteredOptionsOfSearch) }));
  };

  const setSearchQuery = React.useCallback(
    (searchTerm?: string) => {
      setQueryParams({ searchTerm }, 'replaceIn');
    },
    [setQueryParams]
  );

  const clearSearch = ({ resetPage = false }: { resetPage?: boolean } = {}) => {
    setSearchInputValue('');
    setFilteredOptions([]);

    if (isDrawer) {
      return;
    }

    setQueryParams(
      {
        ...(resetPage ? { page: 1 } : {}),
        searchTerm: undefined,
        caseIdsToInclude: undefined,
        slideIdsToInclude: undefined,
      },
      'replaceIn'
    );
  };

  const handleApplyFreeSoloSearch = (search: string) => {
    if (isDrawer) {
      // should update advanced input value
      handleChange(null, search);
    } else if (search === '') {
      clearSearch();
    } else {
      const filteredOptionsOfSearch = filterSearchOptions<CaseSearchItem>(fullSearchKeys, allSearchItems, search);

      const studyFilteredOptions = filter(filteredOptionsOfSearch, (item) => item.studyId === studyId);
      const finalFilteredOptions =
        studyId && !isEmpty(studyFilteredOptions) ? studyFilteredOptions : filteredOptionsOfSearch;

      if (slidesMode) {
        const slideIdsToFilter = map(finalFilteredOptions, (option) => first(option.slides)?.id);
        setQueryParams(
          {
            page: 1,
            searchTerm: search,
            caseIdsToInclude: undefined,
            slideIdsToInclude: isEmpty(slideIdsToFilter) ? undefined : slideIdsToFilter,
          },
          'replaceIn'
        );
      } else {
        const caseIdsToFilter = uniq(map(finalFilteredOptions, (option) => toString(option.caseId)));
        setQueryParams(
          {
            page: 1,
            searchTerm: search,
            slideIdsToInclude: undefined,
            caseIdsToInclude: isEmpty(caseIdsToFilter) ? undefined : caseIdsToFilter,
          },
          'replaceIn'
        );
      }
    }
  };

  const handleApplyOptionDrawer = (event: React.SyntheticEvent<Element, Event>, value: CaseSearchItem | string) => {
    if (!value) {
      handleChange(event, '');
    } else if (isString(value)) {
      handleChange(event, value);
    } else if (slidesMode) {
      handleChange(event, first(value.slides)?.id);
    } else {
      handleChange(event, value?.caseLabel);
    }
  };

  const handleApplyOption: AutocompleteProps<CaseSearchItem | string, false, boolean, any>['onChange'] = (
    event,
    value,
    reason
  ) => {
    if (isDrawer) {
      handleApplyOptionDrawer(event, value);
      return;
    }

    if (!value || reason === 'clear') {
      clearSearch({ resetPage: true });
    } else if (isString(value)) {
      handleApplyFreeSoloSearch(value);
    } else if (slidesMode) {
      const selectedSlideId = first(value.slides)?.id;
      setQueryParams(
        {
          page: 1,
          caseIdsToInclude: undefined,
          slideIdsToInclude: selectedSlideId ? [selectedSlideId] : undefined,
          searchTerm: selectedSlideId,
        },
        'replaceIn'
      );
    } else {
      setQueryParams(
        {
          page: 1,
          slideIdsToInclude: undefined,
          ...(value.caseId ? { caseIdsToInclude: [toString(value.caseId)] } : {}),
          ...(value.caseLabel ? { searchTerm: value.caseLabel } : {}),
        },
        'replaceIn'
      );
    }
  };

  return {
    searchIndexSuccess,
    isSearchIndexLoading,
    isErrorSearchIndex,
    searchInputValue,
    setSearchInputValue,
    searchQuery,
    setSearchQuery,
    filteredOptions,
    setFilteredOptions,
    searchHasNoResults,
    setSearchHasNoResults,
    clearSearch,
    handleSearch,
    handleApplyFreeSoloSearch,
    handleApplyOption,
  };
};
